import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useTrips } from '../../context/TripsContext';
import { useExpenses } from '../../context/ExpensesContext';
import { useAuth } from '../../context/AuthContext';
import { FileText, Plane, Hotel, Receipt, DollarSign, CreditCard, Landmark, User } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { getCountryFromCurrency, getCountryNameFromCurrency } from '../../utils/currencyCountryMapping';
import { useNavigate, useParams } from 'react-router-dom';
import { RefundReportSection, RefundReportSkeleton, RefundReportTotals, RefundReportSummary, ExportButtons } from './components';
import { flightDetailsService, FlightDetails, businessService } from '../../services/supabase';
import type { Business } from '../../services/supabase/business.service';
import { CurrencySelect } from '../../components/CurrencySelect';
import { useConversion } from '../../hooks/useConversion';

// Payment method options with icons - dynamic based on trip setting
const getPaymentMethods = (onlyCooperateCard: boolean) => {
  if (onlyCooperateCard) {
    // When only cooperate card is enabled, show "Cooperate Card" but store as "Card"
    return [
      { value: 'Bank Transfer', icon: Landmark },
      { value: 'Card', icon: CreditCard, displayValue: 'Cooperate Card' },
      { value: 'Personal', icon: User },
    ];
  } else {
    // When both personal and cooperate cards are allowed
    return [
      { value: 'Bank Transfer', icon: Landmark },
      { value: 'Card', icon: CreditCard },
      { value: 'Cooperate Card', icon: CreditCard },
      { value: 'Personal', icon: User },
    ];
  }
};

interface Flight {
  id: string;
  flight: string;
  class: string;
  cost: number;
  currency: string;
  provider: string;
  paymentMethod: string;
  refund: number;
  date: string; // Store the expense date for sorting
  convertedAmount: number;
  convertedCurrency: string;
  destinationCountry: string;
}

interface Accommodation {
  id: string;
  description: string;
  city: string;
  country: string;
  date: string;
  nights: number;
  costPerNight: number;
  currency: string;
  paymentMethod: string;
  refund: number;
  convertedAmount: number;
  convertedCurrency: string;
}

interface DeductibleExpense {
  id: string;
  description: string;
  category: string;
  date: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  refund: number;
  convertedAmount: number;
  convertedCurrency: string;
}

interface PerDiem {
  id: string;
  country: string;
  type: 'With Accommodation' | 'Self Accommodation';
  days: number;
  dailyAmountUSD: number;
}

// Per diem rates by country (in USD)
const PER_DIEM_RATES: Record<string, number> = {
  'US': 50,
  'GB': 50,
  'FR': 50,
  'DE': 50,
  'IT': 50,
  'ES': 50,
  'JP': 50.25,
  'CN': 50.25,
  'AU': 50.25,
  'CA': 50,
  // Default rate for other countries
  'DEFAULT': 50,
};

const getPerDiemRate = (country: string): number => {
  return PER_DIEM_RATES[country.toUpperCase()] || PER_DIEM_RATES['DEFAULT'];
};

// Helper function to extract date from flight description text
const extractDateFromText = (text: string, expenseDate?: string): string | null => {
  if (!text) return null;

  // Get year from expense date for patterns without year
  const yearFromExpense = expenseDate ? new Date(expenseDate).getFullYear() : new Date().getFullYear();

  // Common date patterns
  const patternsWithYear = [
    // ISO format: YYYY-MM-DD or YYYY/MM/DD
    /(\d{4})[-/](\d{1,2})[-/](\d{1,2})/,
    // US format: MM/DD/YYYY or MM-DD-YYYY
    /(\d{1,2})[-/](\d{1,2})[-/](\d{4})/,
    // Format with month names: Jan 15, 2024 or 15 Jan 2024
    /(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+(\d{4})/i,
    /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+(\d{1,2}),?\s+(\d{4})/i,
  ];

  // Patterns without year - we'll infer the year
  const patternsWithoutYear = [
    // Month name followed by day: "Oct 5", "October 5"
    /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+(\d{1,2})/i,
    // Day followed by month name: "5 Oct", "5 October"
    /(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*/i,
  ];

  // Try patterns with year first
  for (const pattern of patternsWithYear) {
    const match = text.match(pattern);
    if (match) {
      try {
        const dateStr = match[0];
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0]; // Return YYYY-MM-DD format
        }
      } catch (e) {
        continue;
      }
    }
  }

  // Try patterns without year, and append the year from expense date
  for (const pattern of patternsWithoutYear) {
    const match = text.match(pattern);
    if (match) {
      try {
        const dateStr = `${match[0]} ${yearFromExpense}`;
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0]; // Return YYYY-MM-DD format
        }
      } catch (e) {
        continue;
      }
    }
  }

  return null;
};

export const RefundReportGlobal = (): React.ReactNode => {
  const { currentTrip } = useTrips();
  const { expenses, loading, fetchTripExpenses, updateExpense } = useExpenses();
  const { supabaseUser } = useAuth();
  const { getLatestRates, convertUsingReportRates } = useConversion();
  const navigate = useNavigate();
  const { tripId } = useParams<{ tripId: string }>();
  const userCurrency = currentTrip?.currency || 'USD';
  const [userBusiness, setUserBusiness] = useState<Business | null>(null);

  // State for report display currency (defaults to user currency)
  const [reportCurrency, setReportCurrency] = useState<string>(userCurrency);

  // State for exchange rates
  const [exchangeRates, setExchangeRates] = useState<Record<string, number>>({});

  // State to track per diem edits
  const [perDiemEdits, setPerDiemEdits] = useState<Record<string, Partial<PerDiem>>>({});

  // State to track accommodation edits
  const [accommodationEdits, setAccommodationEdits] = useState<Record<string, Partial<Accommodation>>>({});

  // State to track deductible expense edits
  const [deductibleExpenseEdits, setDeductibleExpenseEdits] = useState<Record<string, Partial<DeductibleExpense>>>({});

  // State for flight details (loaded from database)
  const [flightDetailsMap, setFlightDetailsMap] = useState<Record<string, FlightDetails>>({});

  // Local state to prevent flickering - start with true, set to false once we have data
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Fetch user's business if they have one
  useEffect(() => {
    const fetchBusiness = async () => {
      if (supabaseUser?.business_id) {
        try {
          const business = await businessService.getBusinessById(supabaseUser.business_id);
          setUserBusiness(business);
        } catch (error) {
          console.error('Error fetching business:', error);
        }
      }
    };

    fetchBusiness();
  }, [supabaseUser?.business_id]);

  // Load exchange rates when report currency changes
  useEffect(() => {
    const loadExchangeRates = async () => {
      try {
        const rates = await getLatestRates(reportCurrency);
        setExchangeRates(rates.rates);
      } catch (error) {
        console.error('Error loading exchange rates:', error);
        // Fallback: use 1:1 rates
        setExchangeRates({ [reportCurrency]: 1.0 });
      }
    };

    loadExchangeRates();
  }, [reportCurrency, getLatestRates]);

  // Update report currency when user currency changes
  useEffect(() => {
    setReportCurrency(userCurrency);
  }, [userCurrency]);

  useEffect(() => {
    const loadData = async () => {
      if (currentTrip?.id) {
        // Load expenses
        await fetchTripExpenses(currentTrip.id);

        // Load flight details
        try {
          const flightDetails = await flightDetailsService.getByTripId(currentTrip.id);
          // Create a map for easy lookup by expense_id
          const detailsMap: Record<string, FlightDetails> = {};
          flightDetails.forEach(detail => {
            detailsMap[detail.expense_id] = detail;
          });
          setFlightDetailsMap(detailsMap);
        } catch (error) {
          console.error('Error loading flight details:', error);
        }
      }
    };

    loadData();
  }, [currentTrip?.id, fetchTripExpenses]);

  // Auto-categorize expenses
  const { flights, accommodations, deductibleExpenses, perDiems } = useMemo(() => {
    const flightsData: Flight[] = [];
    const accommodationsData: Accommodation[] = [];
    const deductibleExpensesData: DeductibleExpense[] = [];

    // Categorize expenses
    expenses.forEach((expense) => {
      const category = expense.category?.toLowerCase() || '';

      if (category.includes('flight')) {
        // Flight expense (includes 0-cost expenses)
        const description = expense.description || 'Flight';

        // Get flight details from the map (if exists)
        const flightDetail = flightDetailsMap[expense.id];

        // Use flight details if available, otherwise extract from description or use defaults
        const flightClass = flightDetail?.flight_class || 'Economy';
        const flightDate = flightDetail?.actual_flight_date || extractDateFromText(description, expense.expense_date) || expense.expense_date;

        flightsData.push({
          id: expense.id,
          flight: description,
          class: flightClass,
          cost: expense.amount,
          currency: expense.currency || 'USD',
          provider: 'Unknown', // Default, could be enhanced with additional fields
          paymentMethod: expense.payment_method || 'Card',
          refund: 0, // To be calculated
          date: flightDate,
          convertedAmount: expense.converted_amount,
          convertedCurrency: expense.converted_currency,
          destinationCountry: flightDetail?.destination_country || '',
        });
      } else if (category.includes('accommodation') || category.includes('lodging') || category.includes('hotel')) {
        // Accommodation expense
        // Use saved city/country if available, otherwise auto-detect
        let city = expense.city || 'N/A';
        let country = expense.country;

        // If country not saved, try to auto-detect from currency
        if (!country) {
          const globalCurrencies = ['USD', 'EUR'];
          const currency = expense.currency || 'USD';
          const isGlobalCurrency = globalCurrencies.includes(currency.toUpperCase());
          country = isGlobalCurrency ? 'N/A' : getCountryNameFromCurrency(currency);
        }

        accommodationsData.push({
          id: expense.id,
          description: expense.description || expense.category || 'Accommodation',
          city: city,
          country: country,
          date: expense.expense_date,
          nights: 1, // Default, could be enhanced
          costPerNight: expense.amount,
          currency: expense.currency || 'USD',
          paymentMethod: expense.payment_method || 'Card',
          refund: 0, // To be calculated
          convertedAmount: expense.converted_amount,
          convertedCurrency: expense.converted_currency,
        });
      } else {
        // All other expenses (deductibles)
        const paymentMethod = expense.payment_method || 'Card';

        // Determine if this is a cooperate card payment
        const onlyCooperateCard = currentTrip?.only_cooperate_card ?? true;
        const isCooperateCardPayment =
          (onlyCooperateCard && paymentMethod === 'Card') ||
          (!onlyCooperateCard && paymentMethod === 'Cooperate Card');

        // Default: 0 for cooperate card, full amount for other payment methods
        const defaultRefund = isCooperateCardPayment ? 0 : expense.converted_amount;

        deductibleExpensesData.push({
          id: expense.id,
          description: expense.description || expense.category || 'Expense',
          category: expense.category || '',
          date: expense.expense_date,
          amount: expense.amount,
          currency: expense.currency || 'USD',
          paymentMethod: paymentMethod,
          refund: defaultRefund,
          convertedAmount: expense.converted_amount,
          convertedCurrency: expense.converted_currency,
        });
      }
    });

    // Calculate per diems by currency and dates
    const currencyDateMap: Record<string, Set<string>> = {};

    expenses.forEach((expense) => {
      const currency = expense.currency || 'USD';
      const dateStr = expense.expense_date.split('T')[0]; // Get date without time

      if (!currencyDateMap[currency]) {
        currencyDateMap[currency] = new Set();
      }
      currencyDateMap[currency].add(dateStr);
    });

    // Convert to per diems, handling overlaps
    const perDiemsData: PerDiem[] = [];
    const processedDates = new Set<string>();

    // Sort currencies by first occurrence to handle overlaps
    const currenciesByFirstDate = Object.entries(currencyDateMap)
      .map(([currency, dates]) => ({
        currency,
        dates: Array.from(dates).sort(),
        firstDate: Array.from(dates).sort()[0],
      }))
      .sort((a, b) => a.firstDate.localeCompare(b.firstDate));

    currenciesByFirstDate.forEach(({ currency, dates }) => {
      // Filter out global currencies (USD, EUR) - they don't indicate a specific country
      const globalCurrencies = ['USD', 'EUR'];
      if (globalCurrencies.includes(currency.toUpperCase())) {
        return;
      }

      // Filter out dates already processed by earlier currencies
      const uniqueDates = dates.filter(date => !processedDates.has(date));

      if (uniqueDates.length > 0) {
        const country = getCountryNameFromCurrency(currency);
        const dailyRate = getPerDiemRate(getCountryFromCurrency(currency));

        perDiemsData.push({
          id: `${currency}-per-diem`,
          country,
          type: 'With Accommodation' as const,
          days: uniqueDates.length,
          dailyAmountUSD: dailyRate,
        });

        // Mark these dates as processed
        uniqueDates.forEach(date => processedDates.add(date));
      }
    });

    // Sort flights by date (earliest first)
    flightsData.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateA - dateB;
    });

    // Infer country for accommodations with N/A
    // If there's a clear country from flights, use it
    const countriesFromFlights = new Set(
      flightsData
        .map(f => f.destinationCountry)
        .filter(c => c && c !== 'Israel' && c !== '')
    );

    if (countriesFromFlights.size === 1) {
      const inferredCountry = Array.from(countriesFromFlights)[0];
      accommodationsData.forEach(acc => {
        if (acc.country === 'N/A') {
          acc.country = inferredCountry;
        }
      });
    }

    return {
      flights: flightsData,
      accommodations: accommodationsData,
      deductibleExpenses: deductibleExpensesData,
      perDiems: perDiemsData,
    };
  }, [expenses, flightDetailsMap, currentTrip?.only_cooperate_card]);

  // Update isInitialLoad when data is available
  useEffect(() => {
    if (!loading && (flights.length > 0 || accommodations.length > 0 || deductibleExpenses.length > 0 || perDiems.length > 0)) {
      setIsInitialLoad(false);
    }
  }, [loading, flights.length, accommodations.length, deductibleExpenses.length, perDiems.length]);

  // Show skeleton if loading OR if it's the initial load
  const showSkeleton = loading || isInitialLoad;

  const convertToReportCurrency = (
    convertedAmount: number,
    convertedCurrency: string
  ): number => {
    return convertUsingReportRates(convertedAmount, convertedCurrency, reportCurrency, exchangeRates);
  };

  // Handler for editing per diem cells
  const handlePerDiemEdit = (rowIndex: number, columnKey: string, newValue: any) => {
    const perDiem = perDiems[rowIndex];
    if (!perDiem) return;

    const updatedEdits = { ...perDiemEdits };

    if (!updatedEdits[perDiem.id]) {
      updatedEdits[perDiem.id] = {};
    }

    // Update the specific field
    if (columnKey === 'days' || columnKey === 'dailyAmountUSD') {
      updatedEdits[perDiem.id][columnKey] = newValue;
    }

    setPerDiemEdits(updatedEdits);
  };

  // Handler for editing flight cells
  const handleFlightEdit = async (rowIndex: number, columnKey: string, newValue: any) => {
    const flight = flights[rowIndex];
    if (!flight) return;

    try {
      // Handle different field types
      if (columnKey === 'paymentMethod') {
        // Save payment_method to expense
        await updateExpense(flight.id, { payment_method: newValue });
      } else if (columnKey === 'class') {
        // Save flight_class to flight_details
        if (!currentTrip?.id) return;
        await flightDetailsService.upsert({
          expense_id: flight.id,
          trip_id: currentTrip.id,
          flight_class: newValue,
        });
        // Update local state
        setFlightDetailsMap(prev => ({
          ...prev,
          [flight.id]: {
            ...prev[flight.id],
            flight_class: newValue,
          } as FlightDetails,
        }));
      } else if (columnKey === 'formattedDate') {
        // Save actual_flight_date to flight_details
        if (!currentTrip?.id) return;
        await flightDetailsService.upsert({
          expense_id: flight.id,
          trip_id: currentTrip.id,
          actual_flight_date: newValue, // newValue is already in ISO format (YYYY-MM-DD)
        });
        // Update local state
        setFlightDetailsMap(prev => ({
          ...prev,
          [flight.id]: {
            ...prev[flight.id],
            actual_flight_date: newValue,
          } as FlightDetails,
        }));
      }
    } catch (error) {
      console.error('Failed to update flight:', error);
      // Optionally show error to user
    }
  };

  // Handler for editing accommodation cells
  const handleAccommodationEdit = async (rowIndex: number, columnKey: string, newValue: any) => {
    const accommodation = accommodations[rowIndex];
    if (!accommodation) return;

    const updatedEdits = { ...accommodationEdits };

    if (!updatedEdits[accommodation.id]) {
      updatedEdits[accommodation.id] = {};
    }

    if (columnKey === 'paymentMethod' || columnKey === 'city' || columnKey === 'country') {
      updatedEdits[accommodation.id][columnKey] = newValue;

      // Save to database
      try {
        await updateExpense(accommodation.id, { [columnKey]: newValue });
      } catch (error) {
        console.error(`Failed to update ${columnKey}:`, error);
      }
    }

    setAccommodationEdits(updatedEdits);
  };

  // Handler for editing deductible expense cells
  const handleDeductibleExpenseEdit = async (rowIndex: number, columnKey: string, newValue: any) => {
    const expense = deductibleExpenses[rowIndex];
    if (!expense) return;

    const updatedEdits = { ...deductibleExpenseEdits };

    if (!updatedEdits[expense.id]) {
      updatedEdits[expense.id] = {};
    }

    if (columnKey === 'paymentMethod') {
      updatedEdits[expense.id][columnKey] = newValue;

      // Save payment_method to database
      try {
        await updateExpense(expense.id, { payment_method: newValue });
      } catch (error) {
        console.error('Failed to update payment method:', error);
      }
    }

    setDeductibleExpenseEdits(updatedEdits);
  };

  // Calculate totals
  const flightTotal = flights.reduce((sum, flight) => sum + convertToReportCurrency(flight.convertedAmount, flight.convertedCurrency), 0);
  const accommodationTotal = accommodations.reduce((sum, acc) => sum + convertToReportCurrency(acc.convertedAmount, acc.convertedCurrency), 0);
  const deductibleTotal = deductibleExpenses.reduce((sum, exp) => sum + convertToReportCurrency(exp.convertedAmount, exp.convertedCurrency), 0);
  const perDiemTotal = perDiems.reduce((sum, pd) => sum + convertToReportCurrency(pd.days * pd.dailyAmountUSD, 'USD'), 0);
  const grandTotal = flightTotal + accommodationTotal + deductibleTotal + perDiemTotal;
  const totalCost = grandTotal; // In global report, refund equals cost since we don't have refund calculations

  // Format dates as Ddd-Mmm-YY
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);
    return `${day}-${month}-${year}`;
  };

  const tripDates = currentTrip?.start_date && currentTrip?.end_date
    ? `${formatDate(currentTrip.start_date)} to ${formatDate(currentTrip.end_date)}`
    : undefined;

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <FileText className="h-6 w-6 sm:h-8 sm:w-8" />
            Global Refund Report
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">Tax refund report for {currentTrip?.nickname || 'your trip'}</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
          <CurrencySelect
            value={reportCurrency}
            onValueChange={setReportCurrency}
            className="w-full sm:w-[180px]"
          />
          <ExportButtons
            tripName={currentTrip?.nickname || 'Trip'}
            tripDates={tripDates}
            tripReason={currentTrip?.description || undefined}
            flights={flights}
            accommodations={accommodations}
            deductibleExpenses={deductibleExpenses}
            perDiems={perDiems}
            currency={reportCurrency}
            flightTotal={flightTotal}
            accommodationTotal={accommodationTotal}
            deductibleTotal={deductibleTotal}
            perDiemTotal={perDiemTotal}
            grandTotal={grandTotal}
            totalCost={totalCost}
            businessName={userBusiness?.name}
            businessLogo={userBusiness?.logo || undefined}
          />
        </div>
      </div>

      {showSkeleton ? (
        <>
          {/* Summary Skeleton */}
          <Card>
            <CardHeader className="pb-3">
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex flex-col items-center justify-center p-6 bg-muted/50 rounded-lg">
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-9 w-40" />
                </div>
                <div className="flex flex-col items-center justify-center p-6 bg-primary/10 rounded-lg border-2 border-primary">
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-9 w-40" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Loading Skeletons */}
          <RefundReportSkeleton
            title="Flights"
            description="Flight bookings and refund information"
            rows={3}
            columns={8}
          />
          <RefundReportSkeleton
            title="Accommodation"
            description="Hotel and lodging expenses"
            rows={3}
            columns={8}
          />
          <RefundReportSkeleton
            title="Deductible Expenses"
            description="Other documented expenses"
            rows={3}
            columns={7}
          />
          <RefundReportSkeleton
            title="Per Diem"
            description="Daily allowances by country"
            rows={2}
            columns={4}
          />

          {/* Totals Skeleton */}
          <Card>
            <CardHeader className="pb-3">
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center justify-between py-2">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-4 w-32" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <>
          {/* Summary Section */}
          <RefundReportSummary
            totalCost={totalCost}
            totalRefund={grandTotal}
            currency={reportCurrency}
          />

          {/* Flights Section */}
          <RefundReportSection
        title="Flights"
        description="Flight bookings and refund information"
        icon={Plane}
        buttonText="Add Your Flights"
        onButtonClick={() => navigate(`/trip/${tripId}/add-expense`)}
        columns={[
          { header: 'Flight', accessor: 'flight', align: 'left', width: '180px', primaryField: true },
          { header: 'Destination', accessor: 'destinationCountry', align: 'left', width: '120px' },
          { header: 'Date', accessor: 'formattedDate', align: 'left', width: '110px', editable: true, columnType: 'date' },
          { header: 'Class', accessor: 'class', align: 'left', width: '100px', editable: true, columnType: 'select', options: ['Economy', 'Premium Economy', 'Business', 'First Class'] },
          { header: 'Cost', accessor: 'cost', align: 'right', width: '100px', primaryField: true },
          { header: 'Currency', accessor: 'currency', align: 'left', width: '80px' },
          { header: `In ${reportCurrency}`, accessor: 'convertedCost', align: 'right', width: '100px' },
          { header: 'Payment Method', accessor: 'paymentMethod', align: 'left', width: '140px', editable: true, columnType: 'select-with-other', optionsWithIcons: getPaymentMethods(currentTrip?.only_cooperate_card ?? true) },
          { header: 'Refund', accessor: 'refund', align: 'right', width: '100px' },
        ]}
        data={flights.map(flight => ({
            ...flight,
            formattedDate: flight.date, // Store ISO format for date picker
            convertedCost: convertToReportCurrency(flight.convertedAmount, flight.convertedCurrency)
        }))}
        onCellEdit={handleFlightEdit}
      />

      {/* Accommodation Section */}
      <RefundReportSection
        title="Accommodation"
        description="Hotel and lodging expenses"
        icon={Hotel}
        buttonText="Add Accommodation"
        onButtonClick={() => navigate(`/trip/${tripId}/add-expense?category=Accommodation`)}
        columns={[
          { header: 'Description', accessor: 'description', align: 'left', width: '200px', primaryField: true },
          { header: 'City', accessor: 'city', align: 'left', width: '150px', editable: true, columnType: 'text' },
          { header: 'Country', accessor: 'country', align: 'left', width: '120px', editable: true, columnType: 'country' },
          { header: 'Nights', accessor: 'nights', align: 'right', width: '80px', primaryField: true },
          { header: 'Cost/Night', accessor: 'costPerNight', align: 'right', width: '100px' },
          { header: 'Currency', accessor: 'currency', align: 'left', width: '80px' },
          { header: `In ${reportCurrency}`, accessor: 'totalConverted', align: 'right', width: '100px', primaryField: true },
          { header: 'Payment Method', accessor: 'paymentMethod', align: 'left', width: '140px', editable: true, columnType: 'select-with-other', optionsWithIcons: getPaymentMethods(currentTrip?.only_cooperate_card ?? true) },
          { header: 'Refund', accessor: 'refund', align: 'right', width: '100px' },
        ]}
        data={accommodations.map(acc => {
          const edits = accommodationEdits[acc.id] || {};
          const paymentMethod = edits.paymentMethod !== undefined ? edits.paymentMethod : acc.paymentMethod;
          const city = edits.city !== undefined ? edits.city : acc.city;
          const country = edits.country !== undefined ? edits.country : acc.country;

          return {
            ...acc,
            paymentMethod: paymentMethod,
            city: city,
            country: country,
            totalConverted: convertToReportCurrency(acc.convertedAmount, acc.convertedCurrency)
          };
        })}
        onCellEdit={handleAccommodationEdit}
      />

      {/* Deductible Expenses Section */}
      <RefundReportSection
        title="Deductible Expenses"
        description="Other documented expenses"
        icon={Receipt}
        buttonText="Add Deductible"
        onButtonClick={() => navigate(`/trip/${tripId}/add-expense`)}
        columns={[
          { header: 'Description', accessor: 'description', align: 'left', width: '200px', primaryField: true },
          { header: 'Date', accessor: 'formattedDate', align: 'left', width: '120px' },
          { header: 'Amount', accessor: 'amount', align: 'right', width: '100px', primaryField: true },
          { header: 'Currency', accessor: 'currency', align: 'left', width: '80px' },
          { header: `In ${reportCurrency}`, accessor: 'convertedAmount', align: 'right', width: '100px' },
          { header: 'Payment Method', accessor: 'paymentMethod', align: 'left', width: '140px', editable: true, columnType: 'select-with-other', optionsWithIcons: getPaymentMethods(currentTrip?.only_cooperate_card ?? true) },
          { header: 'Refund', accessor: 'refund', align: 'right', width: '100px' },
        ]}
        data={deductibleExpenses.map(expense => {
          const edits = deductibleExpenseEdits[expense.id] || {};
          const paymentMethod = edits.paymentMethod !== undefined ? edits.paymentMethod : expense.paymentMethod;

          return {
            ...expense,
            formattedDate: new Date(expense.date).toLocaleDateString(),
            paymentMethod: paymentMethod,
            convertedAmount: convertToReportCurrency(expense.convertedAmount, expense.convertedCurrency)
          };
        })}
        onCellEdit={handleDeductibleExpenseEdit}
      />

      {/* Per Diem Section */}
      <RefundReportSection
        title="Per Diem"
        description="Daily allowances by country"
        icon={DollarSign}
        columns={[
          { header: 'Country', accessor: 'country', align: 'left', width: '180px', primaryField: true },
          { header: 'Type', accessor: 'type', align: 'left', width: '160px', primaryField: true },
          { header: 'Days', accessor: 'days', align: 'right', width: '80px', editable: true, columnType: 'number', primaryField: true },
          { header: 'Daily Amount (USD)', accessor: 'dailyAmountUSD', align: 'right', width: '140px', editable: true, columnType: 'number' },
          { header: `Total (${reportCurrency})`, accessor: 'totalConverted', align: 'right', width: '120px', primaryField: true },
        ]}
        data={perDiems.map(perDiem => {
          // Apply any edits to this per diem
          const edits = perDiemEdits[perDiem.id] || {};
          const days = edits.days !== undefined ? edits.days : perDiem.days;
          const dailyAmountUSD = edits.dailyAmountUSD !== undefined ? edits.dailyAmountUSD : perDiem.dailyAmountUSD;

          const totalUSD = days * dailyAmountUSD;
          return {
            ...perDiem,
            type: perDiem.type,
            days,
            dailyAmountUSD,
            totalConverted: convertToReportCurrency(totalUSD, 'USD')
          };
        })}
        onCellEdit={handlePerDiemEdit}
      />

      {/* Totals Breakdown */}
      <RefundReportTotals
        flightTotal={flightTotal}
        accommodationTotal={accommodationTotal}
        deductibleTotal={deductibleTotal}
        perDiemTotal={perDiemTotal}
        grandTotal={grandTotal}
        currency={reportCurrency}
      />
        </>
      )}
    </div>
  );
};
